import { useState, useEffect, useCallback } from 'react';
import {
  adminInvestorsAPI,
  adminExpensesAPI,
  adminInvestorSettingsAPI,
  adminInvestorDashboardAPI,
} from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, IndianRupee, Phone, Users,
  Plus, Trash2, Edit2, Save, X, PieChart, Receipt, Settings, BarChart3,
  ChevronDown
} from 'lucide-react';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'expenses', label: 'Expenses', icon: Receipt },
  { id: 'investors', label: 'Investors', icon: Users },
  { id: 'settings', label: 'Settings', icon: Settings },
];

function MonthPicker({ value, onChange }) {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    });
  }
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="month-picker"
        className="appearance-none bg-slate-800/60 border border-white/10 text-white text-sm rounded-lg px-3 py-2 pr-8 focus:ring-2 focus:ring-red-500/40 focus:border-red-500/40 outline-none"
      >
        <option value="">All Time</option>
        {months.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'red' }) {
  const colors = {
    red: 'from-red-500/15 to-red-600/5 border-red-500/20',
    green: 'from-emerald-500/15 to-emerald-600/5 border-emerald-500/20',
    blue: 'from-blue-500/15 to-blue-600/5 border-blue-500/20',
    amber: 'from-amber-500/15 to-amber-600/5 border-amber-500/20',
  };
  const iconColors = {
    red: 'text-red-400 bg-red-500/15',
    green: 'text-emerald-400 bg-emerald-500/15',
    blue: 'text-blue-400 bg-blue-500/15',
    amber: 'text-amber-400 bg-amber-500/15',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-5 ${colors[color]}`} data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconColors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-xs text-slate-400 uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white font-['Outfit']">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function DashboardTab({ month, setMonth }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminInvestorDashboardAPI.get(month || undefined);
      setData(res.data);
    } catch { toast.error('Failed to load dashboard'); }
    setLoading(false);
  }, [month]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-7 h-7 border-2 border-red-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const profitPositive = data.net_profit >= 0;
  const expenseCategories = Object.entries(data.expense_breakdown || {});
  const totalPct = data.investor_splits.reduce((s, i) => s + i.percentage, 0);

  return (
    <div className="space-y-6" data-testid="investor-dashboard-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white font-['Outfit']">Financial Overview</h2>
        <MonthPicker value={month} onChange={setMonth} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Phone} label="Total Calls" value={data.total_calls.toLocaleString()} sub={`@ ₹${data.rate_per_call}/call`} color="blue" />
        <StatCard icon={TrendingUp} label="Revenue" value={`₹${data.total_revenue.toLocaleString()}`} color="green" />
        <StatCard icon={TrendingDown} label="Expenses" value={`₹${data.total_expenses.toLocaleString()}`} color="amber" />
        <StatCard icon={IndianRupee} label="Net Profit" value={`₹${data.net_profit.toLocaleString()}`} sub={profitPositive ? 'Profitable' : 'Loss'} color={profitPositive ? 'green' : 'red'} />
      </div>

      {/* Profit Splits */}
      <div className="rounded-xl border border-white/10 bg-slate-900/50 overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <PieChart className="w-4 h-4 text-red-400" />
          <h3 className="text-sm font-semibold text-white">Profit Distribution</h3>
          {totalPct !== 100 && <span className="text-xs text-amber-400 ml-2">(Total: {totalPct}%)</span>}
        </div>
        {data.investor_splits.length === 0 ? (
          <div className="px-5 py-8 text-center text-slate-500 text-sm">No investors configured. Go to the Investors tab to add partners.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {data.investor_splits.map((inv) => (
              <div key={inv.id} className="px-5 py-4 flex items-center justify-between" data-testid={`split-${inv.name.toLowerCase()}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-600/20 flex items-center justify-center text-red-300 text-xs font-bold">
                    {inv.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm text-white font-medium">{inv.name}</div>
                    <div className="text-xs text-slate-500">{inv.percentage}% share</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-lg font-bold font-['Outfit'] ${inv.profit_share >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    ₹{inv.profit_share.toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Expense Breakdown */}
      {expenseCategories.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-slate-900/50 overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5">
            <h3 className="text-sm font-semibold text-white">Expense Breakdown</h3>
          </div>
          <div className="p-5 space-y-3">
            {expenseCategories.sort((a, b) => b[1] - a[1]).map(([cat, amt]) => {
              const pct = data.total_expenses > 0 ? (amt / data.total_expenses) * 100 : 0;
              return (
                <div key={cat}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-300">{cat}</span>
                    <span className="text-slate-400">₹{amt.toLocaleString()} ({pct.toFixed(1)}%)</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ExpensesTab({ month, setMonth }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ category: '', amount: '', date: new Date().toISOString().slice(0, 10), notes: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminExpensesAPI.list(month || undefined);
      setExpenses(res.data);
    } catch { toast.error('Failed to load expenses'); }
    setLoading(false);
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm({ category: '', amount: '', date: new Date().toISOString().slice(0, 10), notes: '' }); setEditId(null); setShowForm(false); };

  const handleSave = async () => {
    if (!form.category || !form.amount || !form.date) { toast.error('Category, amount, and date are required'); return; }
    const payload = { ...form, amount: parseFloat(form.amount) };
    try {
      if (editId) {
        await adminExpensesAPI.update(editId, payload);
        toast.success('Expense updated');
      } else {
        await adminExpensesAPI.create(payload);
        toast.success('Expense added');
      }
      resetForm();
      load();
    } catch { toast.error('Failed to save expense'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await adminExpensesAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const startEdit = (exp) => {
    setForm({ category: exp.category, amount: String(exp.amount), date: exp.date, notes: exp.notes || '' });
    setEditId(exp.id);
    setShowForm(true);
  };

  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="space-y-5" data-testid="expenses-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white font-['Outfit']">Expenses</h2>
          <p className="text-xs text-slate-500 mt-0.5">Total: ₹{total.toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-3">
          <MonthPicker value={month} onChange={setMonth} />
          <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-expense-btn" className="bg-red-600 hover:bg-red-700 text-white text-sm h-9 px-4">
            <Plus className="w-4 h-4 mr-1" /> Add Expense
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5 space-y-4" data-testid="expense-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Category</label>
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Server Costs" data-testid="expense-category-input"
                className="w-full bg-slate-800/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 outline-none focus:ring-2 focus:ring-red-500/40" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Amount (₹)</label>
              <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" data-testid="expense-amount-input"
                className="w-full bg-slate-800/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 outline-none focus:ring-2 focus:ring-red-500/40" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date-input"
                className="w-full bg-slate-800/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-red-500/40" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Notes</label>
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" data-testid="expense-notes-input"
                className="w-full bg-slate-800/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 outline-none focus:ring-2 focus:ring-red-500/40" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} data-testid="save-expense-btn" className="bg-red-600 hover:bg-red-700 text-white text-sm h-9 px-4">
              <Save className="w-4 h-4 mr-1" /> {editId ? 'Update' : 'Save'}
            </Button>
            <Button variant="ghost" onClick={resetForm} className="text-slate-400 hover:text-white text-sm h-9 px-4">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-7 h-7 border-2 border-red-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : expenses.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-slate-900/50 py-12 text-center text-slate-500 text-sm">No expenses recorded {month ? 'for this month' : 'yet'}</div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-slate-900/50 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 text-left">
                <th className="px-5 py-3 text-xs text-slate-400 font-medium">Date</th>
                <th className="px-5 py-3 text-xs text-slate-400 font-medium">Category</th>
                <th className="px-5 py-3 text-xs text-slate-400 font-medium">Amount</th>
                <th className="px-5 py-3 text-xs text-slate-400 font-medium">Notes</th>
                <th className="px-5 py-3 text-xs text-slate-400 font-medium w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {expenses.map((exp) => (
                <tr key={exp.id} className="hover:bg-white/[0.02] transition-colors" data-testid={`expense-row-${exp.id}`}>
                  <td className="px-5 py-3 text-slate-300">{new Date(exp.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  <td className="px-5 py-3 text-white font-medium">{exp.category}</td>
                  <td className="px-5 py-3 text-amber-400 font-medium">₹{exp.amount.toLocaleString()}</td>
                  <td className="px-5 py-3 text-slate-500 truncate max-w-[200px]">{exp.notes || '—'}</td>
                  <td className="px-5 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => startEdit(exp)} data-testid={`edit-expense-${exp.id}`} className="p-1.5 rounded-md hover:bg-white/5 text-slate-400 hover:text-white transition"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(exp.id)} data-testid={`delete-expense-${exp.id}`} className="p-1.5 rounded-md hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InvestorsTab() {
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', percentage: '' });

  const load = async () => {
    setLoading(true);
    try { const res = await adminInvestorsAPI.list(); setInvestors(res.data); }
    catch { toast.error('Failed to load investors'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ name: '', percentage: '' }); setEditId(null); setShowForm(false); };

  const handleSave = async () => {
    if (!form.name || !form.percentage) { toast.error('Name and percentage are required'); return; }
    const pct = parseFloat(form.percentage);
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error('Percentage must be between 0 and 100'); return; }
    try {
      if (editId) {
        await adminInvestorsAPI.update(editId, { name: form.name, percentage: pct });
        toast.success('Investor updated');
      } else {
        await adminInvestorsAPI.create({ name: form.name, percentage: pct });
        toast.success('Investor added');
      }
      resetForm();
      load();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this investor?')) return;
    try { await adminInvestorsAPI.delete(id); toast.success('Removed'); load(); }
    catch { toast.error('Failed to remove'); }
  };

  const startEdit = (inv) => {
    setForm({ name: inv.name, percentage: String(inv.percentage) });
    setEditId(inv.id);
    setShowForm(true);
  };

  const totalPct = investors.reduce((s, i) => s + (i.percentage || 0), 0);

  return (
    <div className="space-y-5" data-testid="investors-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white font-['Outfit']">Investors & Partners</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Total allocation: <span className={totalPct === 100 ? 'text-emerald-400' : 'text-amber-400'}>{totalPct}%</span>
            {totalPct !== 100 && <span className="text-amber-400 ml-1">(should be 100%)</span>}
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-investor-btn" className="bg-red-600 hover:bg-red-700 text-white text-sm h-9 px-4">
          <Plus className="w-4 h-4 mr-1" /> Add Investor
        </Button>
      </div>

      {showForm && (
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5 space-y-4" data-testid="investor-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Investor name" data-testid="investor-name-input"
                className="w-full bg-slate-800/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 outline-none focus:ring-2 focus:ring-red-500/40" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Percentage (%)</label>
              <input type="number" step="0.1" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} placeholder="e.g. 25" data-testid="investor-percentage-input"
                className="w-full bg-slate-800/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 outline-none focus:ring-2 focus:ring-red-500/40" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} data-testid="save-investor-btn" className="bg-red-600 hover:bg-red-700 text-white text-sm h-9 px-4">
              <Save className="w-4 h-4 mr-1" /> {editId ? 'Update' : 'Save'}
            </Button>
            <Button variant="ghost" onClick={resetForm} className="text-slate-400 hover:text-white text-sm h-9 px-4">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-7 h-7 border-2 border-red-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : investors.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-slate-900/50 py-12 text-center text-slate-500 text-sm">No investors added yet</div>
      ) : (
        <div className="space-y-3">
          {investors.map((inv) => (
            <div key={inv.id} className="rounded-xl border border-white/10 bg-slate-900/50 px-5 py-4 flex items-center justify-between" data-testid={`investor-card-${inv.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500/20 to-red-700/20 flex items-center justify-center text-red-300 text-sm font-bold border border-red-500/20">
                  {inv.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm text-white font-medium">{inv.name}</div>
                  <div className="text-xs text-slate-500">Added {new Date(inv.created_at).toLocaleDateString('en-IN')}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-xl font-bold text-white font-['Outfit']">{inv.percentage}%</div>
                  <div className="text-xs text-slate-500">profit share</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(inv)} data-testid={`edit-investor-${inv.id}`} className="p-1.5 rounded-md hover:bg-white/5 text-slate-400 hover:text-white transition"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleDelete(inv.id)} data-testid={`delete-investor-${inv.id}`} className="p-1.5 rounded-md hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}

          {/* Allocation bar */}
          <div className="rounded-xl border border-white/10 bg-slate-900/50 p-5">
            <div className="text-xs text-slate-400 mb-2">Allocation Distribution</div>
            <div className="h-4 bg-slate-800 rounded-full overflow-hidden flex">
              {investors.map((inv, i) => {
                const hues = [0, 210, 150, 280, 30, 330, 60];
                const hue = hues[i % hues.length];
                return (
                  <div key={inv.id} title={`${inv.name}: ${inv.percentage}%`}
                    style={{ width: `${inv.percentage}%`, backgroundColor: `hsl(${hue}, 60%, 55%)` }}
                    className="h-full transition-all first:rounded-l-full last:rounded-r-full" />
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3 mt-3">
              {investors.map((inv, i) => {
                const hues = [0, 210, 150, 280, 30, 330, 60];
                const hue = hues[i % hues.length];
                return (
                  <div key={inv.id} className="flex items-center gap-1.5 text-xs text-slate-400">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: `hsl(${hue}, 60%, 55%)` }} />
                    {inv.name} ({inv.percentage}%)
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [rate, setRate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminInvestorSettingsAPI.get()
      .then(res => { setRate(String(res.data.rate_per_call)); setLoading(false); })
      .catch(() => { setRate('5'); setLoading(false); });
  }, []);

  const handleSave = async () => {
    const val = parseFloat(rate);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid rate'); return; }
    setSaving(true);
    try {
      await adminInvestorSettingsAPI.update({ rate_per_call: val });
      toast.success('Rate updated');
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-7 h-7 border-2 border-red-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="settings-tab">
      <h2 className="text-lg font-semibold text-white font-['Outfit']">Revenue Settings</h2>
      <div className="rounded-xl border border-white/10 bg-slate-900/50 p-6 max-w-md space-y-5">
        <div>
          <label className="text-sm text-slate-300 mb-2 block">Rate Per Call (₹)</label>
          <p className="text-xs text-slate-500 mb-3">Revenue is auto-calculated as: Total Calls x Rate Per Call</p>
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
              <input type="number" step="0.5" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="rate-per-call-input"
                className="w-full bg-slate-800/60 border border-white/10 rounded-lg pl-7 pr-3 py-2.5 text-white text-sm outline-none focus:ring-2 focus:ring-red-500/40" />
            </div>
            <Button onClick={handleSave} disabled={saving} data-testid="save-rate-btn" className="bg-red-600 hover:bg-red-700 text-white text-sm h-10 px-5">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
        <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <p className="text-xs text-blue-300">
            The revenue formula is: <span className="font-mono font-medium text-blue-200">Total Calls x ₹{rate || '0'} = Revenue</span>. 
            Net Profit = Revenue - Total Expenses. Each investor receives their configured percentage of the net profit.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function AdminInvestorPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [month, setMonth] = useState('');

  return (
    <div className="space-y-6" data-testid="admin-investor-page">
      <div>
        <h1 className="text-2xl font-bold text-white font-['Outfit']">Investor Panel</h1>
        <p className="text-sm text-slate-400 mt-1">Track revenue, manage expenses, and view profit distribution</p>
      </div>

      <div className="flex gap-1 bg-slate-900/50 rounded-xl p-1 border border-white/5 w-fit" data-testid="investor-tabs">
        {TABS.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-red-600/15 text-red-300 shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && <DashboardTab month={month} setMonth={setMonth} />}
      {activeTab === 'expenses' && <ExpensesTab month={month} setMonth={setMonth} />}
      {activeTab === 'investors' && <InvestorsTab />}
      {activeTab === 'settings' && <SettingsTab />}
    </div>
  );
}
